import {
  cloneElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import {
  type Placement,
  arrow,
  autoPlacement,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/react'
import { Portal } from '@/components/Portal'
import styles from './Tooltip.module.css'

const staticSides: Record<string, string> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
}

export interface TooltipProps {
  content: ReactNode
  children: ReactElement
  /**
   * When omitted, the side with the most space is used.
   */
  placement?: Placement
  className?: string
}

/**
 * Shows `content` next to the child element while it's hovered or has
 * keyboard focus, until Escape dismisses it. The child receives `ref`, which
 * must reach a DOM element, and `aria-describedby` while the tooltip shows.
 */
export function Tooltip({
  content,
  children,
  placement,
  className,
}: TooltipProps) {
  const { setTriggerEl, setTooltipEl, arrowRef, visible } =
    useHoverTooltip(placement)
  const id = useId()
  const describedBy = (children.props as { 'aria-describedby'?: string })[
    'aria-describedby'
  ]

  return (
    <>
      {cloneElement(children, {
        ref: setTriggerEl,
        'aria-describedby': visible
          ? [describedBy, id].filter(Boolean).join(' ')
          : describedBy,
      } as object)}

      {visible && (
        <Portal>
          <div
            ref={setTooltipEl}
            id={id}
            role="tooltip"
            className={cx(styles.tooltipContainer, className)}
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            <div
              ref={arrowRef}
              style={{
                position: 'absolute',
                width: '10px',
                height: '10px',
                background: 'inherit',
                transform: 'rotate(45deg)',
              }}
            />
            {content}
          </div>
        </Portal>
      )}
    </>
  )
}

function useHoverTooltip(placement: Placement | undefined) {
  const arrowRef = useRef<HTMLDivElement>(null)
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null)
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null)
  const [visible, setVisible] = useState(false)

  const timer = useRef<number>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])

  const updatePosition = useEffectEvent(
    (trigger: HTMLElement, tooltip: HTMLElement) => {
      void computePosition(trigger, tooltip, {
        placement,
        strategy: 'absolute',
        middleware: [
          shift(),
          placement ? flip() : autoPlacement(),
          offset(10),
          arrow({ element: arrowRef }),
        ],
      }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
        Object.assign(tooltip.style, { left: `${x}px`, top: `${y}px` })

        const staticSide = staticSides[finalPlacement.split('-')[0]] ?? ''
        if (middlewareData.arrow && arrowRef.current) {
          const { x: arrowX, y: arrowY } = middlewareData.arrow
          Object.assign(arrowRef.current.style, {
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            [staticSide]: '-5px',
          })
        }
      })
    },
  )

  useEffect(() => {
    if (!triggerEl || !tooltipEl) {
      return
    }

    return autoUpdate(
      triggerEl,
      tooltipEl,
      () => updatePosition(triggerEl, tooltipEl),
      { animationFrame: true },
    )
  }, [triggerEl, tooltipEl])

  // Shown while either holds, so leaving with one doesn't hide it while the
  // other still does.
  const isHovered = useRef(false)
  const isFocused = useRef(false)

  const scheduleVisibility = useEffectEvent(() => {
    clearTimeout(timer.current)
    timer.current = window.setTimeout(() =>
      setVisible(isHovered.current || isFocused.current),
    )
  })

  useEffect(() => {
    if (!triggerEl) {
      return
    }

    const onMouseEnter = () => {
      isHovered.current = true
      scheduleVisibility()
    }
    const onMouseLeave = () => {
      isHovered.current = false
      scheduleVisibility()
    }
    // Only keyboard focus: a click focuses the trigger too, and hovering
    // already shows the tooltip then.
    const onFocusIn = (event: FocusEvent) => {
      if (
        event.target instanceof Element &&
        event.target.matches(':focus-visible')
      ) {
        isFocused.current = true
        scheduleVisibility()
      }
    }
    const onFocusOut = (event: FocusEvent) => {
      if (
        event.relatedTarget instanceof Node &&
        triggerEl.contains(event.relatedTarget)
      ) {
        return
      }
      isFocused.current = false
      scheduleVisibility()
    }

    triggerEl.addEventListener('mouseenter', onMouseEnter)
    triggerEl.addEventListener('mouseleave', onMouseLeave)
    triggerEl.addEventListener('focusin', onFocusIn)
    triggerEl.addEventListener('focusout', onFocusOut)
    return () => {
      triggerEl.removeEventListener('mouseenter', onMouseEnter)
      triggerEl.removeEventListener('mouseleave', onMouseLeave)
      triggerEl.removeEventListener('focusin', onFocusIn)
      triggerEl.removeEventListener('focusout', onFocusOut)
    }
  }, [triggerEl])

  useEffect(() => {
    if (!triggerEl || !tooltipEl) {
      return
    }

    const hideOnOutsidePress = (event: MouseEvent) => {
      const target = event.composedPath?.()?.[0] || event.target
      if (
        target instanceof Node &&
        !tooltipEl.contains(target) &&
        !triggerEl.contains(target)
      ) {
        isHovered.current = false
        isFocused.current = false
        scheduleVisibility()
      }
    }

    // Capture phase, so the Escape that dismisses a tooltip shown for keyboard
    // focus doesn't also close a dialog around the trigger. A hovered one lets
    // it through: the pointer resting on any trigger must not block Escape.
    const hideOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isFocused.current) {
          event.stopPropagation()
        }
        isHovered.current = false
        isFocused.current = false
        scheduleVisibility()
      }
    }

    document.addEventListener('mousedown', hideOnOutsidePress)
    document.addEventListener('keydown', hideOnEscape, true)
    return () => {
      document.removeEventListener('mousedown', hideOnOutsidePress)
      document.removeEventListener('keydown', hideOnEscape, true)
    }
  }, [triggerEl, tooltipEl])

  return { setTriggerEl, setTooltipEl, arrowRef, visible }
}
